/**
 * Check if a query string is a subsequence of a target string.
 * All characters of query must appear in target in the same order.
 */
export function isSubsequence(query: string, lower: string): boolean {
  if (query.length > lower.length) return false;

  let q = 0;

  for (let t = 0; t < lower.length; t++) {
    if (lower[t] === query[q] && ++q === query.length) {
      return true;
    }
  }

  return false;
}

/**
 * Fuzzy-filter a list of items by keyword using custom fast hybrid subsequence matching.
 *
 * Returns the full list when keyword is empty/blank, otherwise
 * returns items that match the keyword, ordered by relevance:
 * 1. Exact matches
 * 2. Substring matches
 * 3. Subsequence matches
 *
 * @param items     - The array to filter
 * @param keyword   - The search term (empty → returns all items)
 * @param keys      - Object keys to search against (e.g. ["name", "title"])
 * @param options   - Unused option parameter for backward compatibility
 */
export function fuzzyFilter<T>(
  items: T[],
  keyword: string,
  keys: string[],
  options?: any
): T[] {
  const trimmed = keyword?.trim().toLowerCase();
  if (!trimmed || items.length === 0) {
    return items;
  }

  const exactMatches: T[] = [];
  const substringMatches: T[] = [];
  const subsequenceMatches: T[] = [];

  for (let i = 0; i < items.length; i++) {
    const item = items[i];

    // Combine text from the specified keys
    let combined = "";
    for (let k = 0; k < keys.length; k++) {
      const key = keys[k];
      const val = (item as any)[key];
      if (val !== undefined && val !== null) {
        combined += (combined ? " " : "") + String(val);
      }
    }

    const lower = combined.toLowerCase();

    if (lower === trimmed) {
      exactMatches.push(item);
    } else if (lower.includes(trimmed)) {
      substringMatches.push(item);
    } else if (isSubsequence(trimmed, lower)) {
      subsequenceMatches.push(item);
    }
  }

  return [...exactMatches, ...substringMatches, ...subsequenceMatches];
}
